import React, { useMemo } from "react";
import { Box, Grid, Paper, Typography, TextField, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    ExtraCostItem,
    ExtraCostKind,
    ExtraCostsValue,
    hasAmount,
    hasAttachment,
    validateExtraCosts,
} from "./extraCosts";

interface ExtraCostsBoxProps {
    value: ExtraCostsValue;
    onChange: (value: ExtraCostsValue, isValid: boolean) => void;
    disabled?: boolean;
}

/**
 * LPP-22962 (RDT-LPP-22291 §3 SCFE-1): due box extra costi manuali al check-in — lavaggio
 * (negligenza) e recupero vettura. Ogni box: descrizione + importo manuale + upload allegato,
 * con validazione allegato OBBLIGATORIO quando l'importo è valorizzato (AS-09).
 *
 * <p>L'importo è inserito a mano dall'operatore (nessun pricing PRC); gli addebiti bypassano
 * incondizionatamente Super Cover/SCDW lato NTW (penalità T&C). L'allegato confluisce nella sezione
 * allegati del check-in (S3/DocumentEntity lato NTW).</p>
 */
const ExtraCostsBox = ({ value, onChange, disabled = false }: ExtraCostsBoxProps) => {
    const theme = useTheme();
    const { t } = useTranslation(["network", "translation"]);

    const emit = (next: ExtraCostsValue) => onChange(next, validateExtraCosts(next));

    const updateItem = (kind: ExtraCostKind, patch: Partial<ExtraCostItem>) => {
        emit({ ...value, [kind]: { ...value[kind], ...patch } });
    };

    const parseAmount = (raw: string): number | "" => {
        if (raw.trim() === "") {
            return "";
        }
        const cents = Math.round(parseFloat(raw.replace(",", ".")) * 100);
        return Number.isFinite(cents) && cents >= 0 ? cents : "";
    };

    const amountToInput = (amount: number | ""): string =>
        typeof amount === "number" ? (amount / 100).toString() : "";

    const renderBox = (kind: ExtraCostKind) => {
        const item = value[kind];
        const attachmentMissing = hasAmount(item) && !hasAttachment(item);

        return (
            <Grid item xs={12} md={6} key={kind}>
                <Paper
                    variant="outlined"
                    data-testid={`extra-cost-box-${kind}`}
                    sx={{ p: theme.spacing(2) }}
                >
                    <Typography variant="subtitle1" sx={{ mb: theme.spacing(1) }}>
                        {t(`raCheckOutIn.extraCosts.${kind}.title`)}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={disabled}
                        label={t("raCheckOutIn.extraCosts.field.description")}
                        inputProps={{ "data-testid": `extra-cost-${kind}-description` }}
                        value={item.description}
                        onChange={(e) => updateItem(kind, { description: e.target.value })}
                        sx={{ mb: theme.spacing(2) }}
                    />

                    <TextField
                        fullWidth
                        type="number"
                        disabled={disabled}
                        label={t("raCheckOutIn.extraCosts.field.amount")}
                        inputProps={{ "data-testid": `extra-cost-${kind}-amount`, min: 0, step: "0.01" }}
                        value={amountToInput(item.amount)}
                        onChange={(e) => updateItem(kind, { amount: parseAmount(e.target.value) })}
                        sx={{ mb: theme.spacing(2) }}
                    />

                    <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
                        <Button
                            component="label"
                            variant="outlined"
                            color="primary"
                            disabled={disabled}
                            data-testid={`extra-cost-${kind}-upload`}
                        >
                            {t("raCheckOutIn.extraCosts.field.upload")}
                            <input
                                hidden
                                type="file"
                                accept="image/*,application/pdf"
                                data-testid={`extra-cost-${kind}-file-input`}
                                onChange={(e) =>
                                    updateItem(kind, { attachment: e.target.files?.[0] ?? null })
                                }
                            />
                        </Button>
                        {hasAttachment(item) && (
                            <Typography variant="body2" data-testid={`extra-cost-${kind}-file-name`}>
                                {item.attachment && ('name' in item.attachment
                                    ? item.attachment.name
                                    : item.attachment.documentTitle)}
                            </Typography>
                        )}
                    </Box>

                    {attachmentMissing && (
                        <Typography
                            variant="caption"
                            color="error"
                            data-testid={`extra-cost-${kind}-attachment-error`}
                            sx={{ display: "block", mt: theme.spacing(1) }}
                        >
                            {t("raCheckOutIn.extraCosts.error.attachmentRequired")}
                        </Typography>
                    )}
                </Paper>
            </Grid>
        );
    };

    const boxes = useMemo(() => (["wash", "recovery"] as ExtraCostKind[]), []);

    return (
        <Paper elevation={1} sx={{ p: theme.spacing(2), mb: theme.spacing(2) }} data-testid="extra-costs-box">
            <Typography variant="h6" sx={{ mb: theme.spacing(2) }}>
                {t("raCheckOutIn.extraCosts.header.title")}
            </Typography>
            <Grid container columnSpacing={2} rowSpacing={2}>
                {boxes.map(renderBox)}
            </Grid>
        </Paper>
    );
};

export default ExtraCostsBox;
